import API from "@/api/index.js";
import Select from "@/components/common/Select";
import Schedule from "@/lib/sched/Schedule.jsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@/components/common/Button";
import { Popover } from "@headlessui/react";
import Box from "@/components/common/Box";
import DayJS from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useCallback, useMemo, useState } from "react";
import DataBox from "@/components/common/DataBox";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";

DayJS.extend(utc);

const ScheduleRota = () => {
  const queryClient = useQueryClient();

  const [rotaMenu, setRotaMenu] = useState(false);
  const [teamMember, setTeamMember] = useState("");
  const [rota, setRota] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const currentMonth = selectedDate.getMonth();
  const currentYear = selectedDate.getFullYear();

  const rotasFeed = useQuery({
    queryKey: ["rotas"],
    queryFn: () => API.rotas.list(),
    staleTime: 2000,
    refetchOnWindowFocus: true,
  });
  const rotaSchedule = useQuery({
    queryKey: ["rotas", "schedules", currentMonth, currentYear, rota],
    queryFn: () =>
      API.rotas.scheduleMonthView(rota, currentMonth + 1, currentYear),
    enabled: rota !== "",
    staleTime: 2000,
    refetchOnWindowFocus: true,
  });
  const teamMembersFeed = useQuery({
    queryKey: ["allUsersFeed"],
    queryFn: () => API.member.all(),
    staleTime: 2000,
    refetchOnWindowFocus: true,
  });

  const reloadScheduleData = () =>
    queryClient.invalidateQueries(["rotas", "schedules"]);

  const theRota = useMemo(() => {
    if (rotasFeed.isSuccess) {
      return rotasFeed.data.find((item) => item._id === rota) || {};
    }
    return {};
  }, [rotasFeed.isSuccess, rotasFeed.data, rota]);

  const bookingData = useMemo(() => {
    return {
      shiftData: rotaSchedule.isSuccess
        ? rotaSchedule.data.shifts.map((item, index) => {
            return {
              id: index,
              ...item,
            };
          })
        : [],
      bookedSlots: rotaSchedule.isSuccess
        ? rotaSchedule.data.assignedHours.reduce((slots, item) => {
            const dateUnix = DayJS(item.date).startOf("day").unix() * 1000;
            const slotExists =
              slots.filter((a) => a.date === dateUnix).length !== 0;
            if (!slotExists) {
              slots.push({
                date: dateUnix,
                slots: [],
              });
            }

            slots = slots.map((a) => {
              if (a.date !== dateUnix) return a;
              return {
                ...a,
                slots: a.slots.concat([DayJS(item.date).format("HH:mm")]),
              };
            });

            return slots;
          }, [])
        : [],
      slots: [
        "00:00",
        "01:00",
        "02:00",
        "03:00",
        "04:00",
        "05:00",
        "06:00",
        "07:00",
        "08:00",
        "09:00",
        "10:00",
        "11:00",
        "12:00",
        "13:00",
        "14:00",
        "15:00",
        "16:00",
        "17:00",
        "18:00",
        "19:00",
        "20:00",
        "21:00",
        "22:00",
        "23:00",
      ],
    };
  }, [rotaSchedule.isSuccess, rotaSchedule.data]);
  const filteredTeamMembers = useMemo(() => {
    if (teamMembersFeed.isSuccess && theRota) {
      return teamMembersFeed.data
        .filter(
          (item) =>
            item.Roles.map((a) => a?.role?.name).indexOf(theRota?.role) !== -1
        )
        .map((item) => {
          return {
            key: item._id,
            value: item.centralId,
            text: item.firstName + " " + item.lastName,
          };
        });
    }
    return [];
  }, [teamMembersFeed.isSuccess, teamMembersFeed.data, theRota]);

  const findShiftRecords = (start, end) => {
    return rotaSchedule.data.assignedHours.filter((a) => {
      const selectedSlot = DayJS(a.date);
      return (
        (selectedSlot.isAfter(start) || selectedSlot.isSame(start)) &&
        (selectedSlot.isBefore(end) || selectedSlot.isSame(end))
      );
    });
  };

  const onDayTimePress = (date, shiftData) => {
    const records = findShiftRecords(
      shiftData.shift.startAt,
      shiftData.shift.endAt
    );
    const totalShiftHours = DayJS(shiftData.shift.endAt)
      .add(1, "minute")
      .diff(DayJS(shiftData.shift.startAt), "hours");
    const dateRecord = records.filter((a) =>
      DayJS(date).isSame(DayJS(a.date), "hour")
    );

    setRotaMenu({
      alreadyHasAssignment: records.length > 0,
      hasSpace: records.length !== totalShiftHours,
      action: dateRecord[0] === undefined ? "fill" : "",
      date,
      shift: shiftData.shift,
    });
  };
  const renderInsideBox = useCallback(
    (day, time, shiftData) => {
      const timeSplit = time.split(":");
      const date = DayJS(selectedDate)
        .set("date", day)
        .startOf("day")
        .set("hours", timeSplit[0])
        .set("minutes", 0)
        .set("seconds", 0);

      if (rotaSchedule.isSuccess) {
        const records = rotaSchedule.data.assignedHours.filter((a) => {
          return date.isSame(DayJS(a.date), "hour");
        });

        if (records.length > 0) {
          if (records[0].teamMember.firstName !== undefined) {
            return (
              <Popover className="bg- relative">
                <Popover.Button>
                  <div className="my-auto flex h-full flex-col items-center justify-center  text-xs">
                    <div className="text-xs ">
                      {records[0].teamMember.firstName
                        .substring(0, 1)
                        .toUpperCase()}
                      {records[0].teamMember.lastName
                        .substring(0, 1)
                        .toUpperCase()}
                    </div>
                    <div className="break-all text-[9px]">
                      {records[0].teamMember.callsign}
                    </div>
                  </div>
                </Popover.Button>
                <Popover.Panel>
                  <Link to={`/team/${records[0].teamMember._id}`}>
                    <div className="text-xs">
                      <DataBox
                        title={"Name"}
                        value={
                          <div className="underline">
                            {records[0].teamMember.firstName +
                              " " +
                              records[0].teamMember.lastName}
                          </div>
                        }
                        v
                      />
                      <DataBox
                        title={"TeamID"}
                        value={records[0].teamMember.callsign}
                        v
                      />
                      <DataBox
                        title={"Phone"}
                        value={records[0].teamMember.phone}
                        v
                      />
                      <DataBox
                        title={"Email"}
                        value={records[0].teamMember.email}
                        v
                      />
                    </div>
                  </Link>
                </Popover.Panel>
              </Popover>
            );
          }
        }
      }

      return <div>{/*{day}*/}</div>;
    },
    [rotaSchedule.isSuccess, rotaSchedule.data]
  );

  const handleSaveShift = () => {
    const completeHook = () => {
      setRotaMenu(false);
      setTeamMember("");
      reloadScheduleData();
    };

    const hours = [];
    let hourSlot = DayJS(rotaMenu.shift.startAt);
    while (hourSlot.isBefore(DayJS(rotaMenu.shift.endAt))) {
      hours.push(hourSlot.toISOString());
      hourSlot = hourSlot.add(1, "hour");
    }

    if (rotaMenu.alreadyHasAssignment) {
      if (rotaMenu.action === "") {
        toast.error("You must first select an action to complete.");
        return;
      } else if (rotaMenu.action === "fill") {
        const missingSlots = [];
        const existingRecords = findShiftRecords(
          rotaMenu.shift.startAt,
          rotaMenu.shift.endAt
        );
        for (const hour of hours) {
          if (existingRecords.filter((a) => a.date === hour).length === 0) {
            missingSlots.push(hour);
          }
        }

        if (teamMember === "") {
          toast.error("You must first select an Team Member.");
          return;
        }

        API.rotas
          .assignShift(rota, {
            centralId: teamMember,
            hours: missingSlots,
          })
          .then(() => {
            completeHook();
            toast.success("Successfully updated the rota shift");
          })
          .catch((err) => {
            toast.error("There was an unexpected server error.");
          });
        return;
      } else if (rotaMenu.action === "remove_whole") {
        API.rotas
          .unassignShift(rota, {
            hours,
          })
          .then(() => {
            completeHook();
            toast.success("Successfully cleared shift");
          })
          .catch((err) => {
            toast.error("There was an unexpected server error.");
          });
        return;
      } else if (rotaMenu.action === "remove_hour") {
        API.rotas
          .unassignShift(rota, {
            hours: [rotaMenu.date],
          })
          .then(() => {
            completeHook();
            toast.success("Successfully cleared shift slot");
          })
          .catch((err) => {
            toast.error("There was an unexpected server error.");
          });
        return;
      }
    }

    if (teamMember === "") {
      toast.error("You must first select an Team Member.");
      return;
    }
    API.rotas
      .assignShift(rota, {
        centralId: teamMember,
        hours,
      })
      .then(() => {
        completeHook();
        toast.success("Successfully updated the rota shift");
      })
      .catch((err) => {
        toast.error("There was an unexpected server error.");
      });
  };

  return (
    <div className="w-full p-2">
      <div className="w-full p-4">
        <Select
          label={"Role"}
          options={
            rotasFeed.isSuccess &&
            rotasFeed.data.map((item, key) => ({
              key: key._id,
              text: item.title,
              value: item._id,
            }))
          }
          value={rota || ""}
          onChange={(e) => {
            setRota(e.target.value);
            setRotaMenu(false);
          }}
        />
      </div>
      {rotaMenu !== false && (
        <Box title={"Rota Shift"} className={"my-2"} styles="p-4">
          <div className="text-center text-muted-foreground">
            {rotaMenu.shift.title}:{" "}
            <span className=" text-foreground">
              {DayJS(rotaMenu.shift.startAt).format("DD-MM-YYYY HH:mm")} -&gt;{" "}
              {DayJS(rotaMenu.shift.endAt).format("DD-MM-YYYY HH:mm")}
            </span>
          </div>
          {rotaMenu.alreadyHasAssignment && (
            <div className="px-2 py-4">
              <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-4 ">
                <Button
                  onClick={() =>
                    setRotaMenu({
                      ...rotaMenu,
                      action: "fill",
                    })
                  }
                  active={rotaMenu.action === "fill"}
                  size="sm"
                  text={"Assign empty shift(s)"}
                />
                <Button
                  onClick={() =>
                    setRotaMenu({
                      ...rotaMenu,
                      action: "remove_whole",
                    })
                  }
                  active={rotaMenu.action === "remove_whole"}
                  size="sm"
                  text={"Clear Shift"}
                />
                <Button
                  onClick={() =>
                    setRotaMenu({
                      ...rotaMenu,
                      action: "swap",
                    })
                  }
                  active={rotaMenu.action === "swap"}
                  size="sm"
                  text={"Swap entire shift"}
                />
                <Button
                  onClick={() =>
                    setRotaMenu({
                      ...rotaMenu,
                      action: "remove_hour",
                    })
                  }
                  active={rotaMenu.action === "remove_hour"}
                  size="sm"
                  text={`Clear just ${DayJS(rotaMenu.date).format(
                    "DD-MM-YYYY HH:mm"
                  )}`}
                />
              </div>
            </div>
          )}
          {(!rotaMenu.alreadyHasAssignment ||
            rotaMenu.action === "fill" ||
            rotaMenu.action === "swap") && (
            <div className="mx-auto w-1/2 p-4">
              <Select
                label={"Team Member"}
                options={filteredTeamMembers}
                value={teamMember || ""}
                onChange={(e) => setTeamMember(e.target.value)}
              />
            </div>
          )}
          <div className={"mt-2 flex justify-between border-t p-4"}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setRotaMenu(false)}
              text={"Close"}
            />
            <Button
              type="button"
              variant="success"
              onClick={handleSaveShift}
              text={"Update"}
            />
          </div>
        </Box>
      )}

      {rota !== "" ? (
        <Schedule
          bookingData={bookingData}
          renderInsideBox={renderInsideBox}
          onDayTimePress={onDayTimePress}
          onDateChange={(a) => {
            setSelectedDate(a);
            setRotaMenu(false);
          }}
        />
      ) : (
        <div className={"my-2 rounded border p-2 text-center"}>
          Please select a rota to view.
        </div>
      )}
    </div>
  );
};
export default ScheduleRota;
